<admin-app-header></admin-app-header>
<app-enterprise-admin-bar></app-enterprise-admin-bar>

<div class="container-fluid">
    <div class="row header">
        <div class="col-12">
            <h2 class="mb-3">Redefine Parking Importer</h2>
            <p class="text-muted">Import properties from Redefine API into a portfolio</p>
        </div>
    </div>

    <!-- Step 1: Select Portfolio and Sectors -->
    <div class="row mb-4">
        <div class="col-12">
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="mb-0">Step 1: Select Portfolio & Sectors</h5>
                    <span *ngIf="enterpriseContext.currentEnterprise" class="badge" [style.background-color]="enterpriseContext.currentEnterprise.primaryColor">
                        {{ enterpriseContext.currentEnterprise.name }}
                    </span>
                    <span *ngIf="!enterpriseContext.currentEnterprise" class="badge bg-secondary">
                        All Enterprises
                    </span>
                </div>
                <div class="card-body">
                    <div class="row mb-3">
                        <!-- Portfolio Selection -->
                        <div class="col-md-6">
                            <label for="portfolioSelect" class="form-label">Target Portfolio</label>
                            <select
                                id="portfolioSelect"
                                class="form-select"
                                [(ngModel)]="selectedPortfolioKey"
                                (ngModelChange)="onPortfolioChange()"
                                [disabled]="isLoadingPortfolios || isImporting || portfolios.length === 0"
                            >
                                <option value="">-- Select a Portfolio --</option>
                                <option *ngFor="let portfolio of portfolios" [value]="portfolio.$key">
                                    {{ portfolio.companyName }}
                                </option>
                            </select>
                            <small *ngIf="isLoadingPortfolios" class="text-muted">Loading portfolios...</small>
                            <small *ngIf="!isLoadingPortfolios && portfolios.length === 0" class="text-warning">
                                <i class="fas fa-exclamation-triangle me-1"></i>
                                No portfolios found for the selected enterprise. Select a different enterprise above.
                            </small>
                            <small *ngIf="!isLoadingPortfolios && portfolios.length > 0" class="text-muted">
                                {{ portfolios.length }} portfolio(s) available
                            </small>
                        </div>
                        
                        <!-- Sector Filter -->
                        <div class="col-md-6">
                            <label class="form-label">Filter by Sector</label>
                            <div *ngIf="isLoadingSectors" class="text-muted p-2 border rounded">
                                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                                Loading sectors...
                            </div>
                            <div *ngIf="!isLoadingSectors && sectors.length === 0" class="text-muted p-2 border rounded">
                                No sectors available
                            </div>
                            <div *ngIf="!isLoadingSectors && sectors.length > 0" class="sector-multi-select border rounded p-2" style="max-height: 150px; overflow-y: auto;">
                                <div *ngFor="let sector of sectors" class="form-check mb-2">
                                    <input
                                        type="checkbox"
                                        class="form-check-input"
                                        [id]="'sector-' + sector.SectorId"
                                        [checked]="isSectorSelected(sector.SectorId)"
                                        (change)="toggleSectorSelection(sector.SectorId)"
                                        [disabled]="isImporting"
                                    />
                                    <label class="form-check-label" [for]="'sector-' + sector.SectorId">
                                        <strong>{{ sector.SectorName }}</strong>
                                        <span *ngIf="sector.PropertyCount" class="badge bg-secondary ms-2">{{ sector.PropertyCount }}</span>
                                    </label>
                                </div>
                            </div>
                            <small class="text-muted d-block mt-1">
                                <i class="fas fa-info-circle me-1"></i>
                                {{ selectedSectorIds.size === 0 ? 'No filter - all sectors will be fetched' : selectedSectorIds.size + ' sector(s) selected' }}
                            </small>
                        </div>
                    </div>
                    
                    <!-- Fetch Button -->
                    <div class="row">
                        <div class="col-12">
                            <button
                                class="btn btn-primary"
                                (click)="fetchProperties()"
                                [disabled]="!selectedPortfolioKey || isLoadingProperties || isImporting"
                            >
                                <i *ngIf="!isLoadingProperties" class="fas fa-download me-2"></i>
                                <span *ngIf="isLoadingProperties" class="spinner-border spinner-border-sm me-2" role="status"></span>
                                {{ isLoadingProperties ? 'Fetching Properties...' : 'Fetch Properties from Redefine' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Step 2: Select Properties -->
    <div class="row mb-4" *ngIf="properties.length > 0">
        <div class="col-12">
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="mb-0">Step 2: Select Properties to Import</h5>
                    <div>
                        <span class="badge bg-info me-3">{{ properties.length }} properties found</span>
                        <span class="badge bg-primary">{{ selectedCount }} selected</span>
                    </div>
                </div>
                <div class="card-body">
                    <!-- Selection controls -->
                    <div class="mb-3">
                        <button class="btn btn-sm btn-outline-secondary me-2" (click)="selectAll()" [disabled]="isImporting">
                            <i class="fas fa-check-square me-1"></i> Select All
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" (click)="deselectAll()" [disabled]="isImporting">
                            <i class="fas fa-square me-1"></i> Deselect All
                        </button>
                    </div>

                    <!-- Properties table -->
                    <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
                        <table class="table table-hover">
                            <thead class="sticky-top bg-white">
                                <tr>
                                    <th style="width: 50px;">
                                        <input
                                            type="checkbox"
                                            class="form-check-input"
                                            [checked]="selectedCount === properties.length && properties.length > 0"
                                            [indeterminate]="selectedCount > 0 && selectedCount < properties.length"
                                            (change)="selectedCount === properties.length ? deselectAll() : selectAll()"
                                            [disabled]="isImporting"
                                        />
                                    </th>
                                    <th style="width: 80px;">Image</th>
                                    <th>Name</th>
                                    <th>Address</th>
                                    <th>Sector</th>
                                    <th style="width: 80px;">ID</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let property of properties" [class.table-primary]="isPropertySelected(property.PropertyId)">
                                    <td>
                                        <input
                                            type="checkbox"
                                            class="form-check-input"
                                            [checked]="isPropertySelected(property.PropertyId)"
                                            (change)="togglePropertySelection(property.PropertyId)"
                                            [disabled]="isImporting"
                                        />
                                    </td>
                                    <td>
                                        <img
                                            *ngIf="getPropertyImage(property)"
                                            [src]="getPropertyImage(property)"
                                            alt="{{ property.PropertyName }}"
                                            class="property-thumbnail"
                                        />
                                        <span *ngIf="!getPropertyImage(property)" class="text-muted">
                                            <i class="fas fa-image"></i>
                                        </span>
                                    </td>
                                    <td>
                                        <strong>{{ property.PropertyName }}</strong>
                                        <br />
                                        <small class="text-muted" *ngIf="property.Description">
                                            {{ property.Description | slice : 0 : 100 }}{{ property.Description.length > 100 ? '...' : '' }}
                                        </small>
                                    </td>
                                    <td>{{ getPropertyAddress(property) }}</td>
                                    <td>{{ property.SectorName || '-' }}</td>
                                    <td>
                                        <small class="text-muted">{{ property.PropertyId }}</small>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Step 3: Import -->
    <div class="row mb-4" *ngIf="properties.length > 0 && selectedCount > 0">
        <div class="col-12">
            <div class="card">
                <div class="card-header">
                    <h5 class="mb-0">Step 3: Import Selected Properties</h5>
                </div>
                <div class="card-body">
                    <div class="d-flex align-items-center">
                        <button
                            class="btn btn-success btn-lg me-3"
                            (click)="importSelected()"
                            [disabled]="isImporting || selectedCount === 0"
                        >
                            <i *ngIf="!isImporting" class="fas fa-upload me-2"></i>
                            <span *ngIf="isImporting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            {{ isImporting ? 'Importing...' : 'Import ' + selectedCount + ' Properties' }}
                        </button>
                        <span class="text-muted">
                            Properties will be imported as <strong>Private</strong> status. You can publish them from the Buildings list.
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Import Results -->
    <div class="row mb-4" *ngIf="lastImportResult">
        <div class="col-12">
            <div class="card" [class.border-success]="lastImportResult.success" [class.border-warning]="!lastImportResult.success">
                <div class="card-header">
                    <h5 class="mb-0">Import Results</h5>
                </div>
                <div class="card-body">
                    <div class="row mb-3">
                        <div class="col-md-4 text-center">
                            <h3 class="text-success mb-0">{{ lastImportResult.created }}</h3>
                            <small class="text-muted">Created</small>
                        </div>
                        <div class="col-md-4 text-center">
                            <h3 class="text-danger mb-0">{{ lastImportResult.failed }}</h3>
                            <small class="text-muted">Failed</small>
                        </div>
                        <div class="col-md-4 text-center">
                            <h3 class="mb-0">{{ lastImportResult.totalProcessed }}</h3>
                            <small class="text-muted">Total</small>
                        </div>
                    </div>

                    <!-- Detailed results table -->
                    <div class="table-responsive" *ngIf="lastImportResult.results.length > 0">
                        <table class="table table-sm">
                            <thead>
                                <tr>
                                    <th>Property</th>
                                    <th>Action</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let result of lastImportResult.results" [class.table-danger]="!result.success">
                                    <td>{{ result.propertyName }}</td>
                                    <td>
                                        <span class="badge" [class.bg-success]="result.action === 'created'" [class.bg-secondary]="result.action === 'skipped'">
                                            {{ result.action }}
                                        </span>
                                    </td>
                                    <td>
                                        <span *ngIf="result.success" class="text-success">
                                            <i class="fas fa-check me-1"></i> Success
                                        </span>
                                        <span *ngIf="!result.success" class="text-danger">
                                            <i class="fas fa-times me-1"></i> {{ result.error || 'Failed' }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Info card -->
    <div class="row">
        <div class="col-12">
            <div class="card">
                <div class="card-header">
                    <h5 class="mb-0">Information</h5>
                </div>
                <div class="card-body">
                    <div class="alert alert-info mb-0">
                        <i class="fas fa-info-circle me-2"></i>
                        <strong>How it works:</strong>
                        <ul class="mb-0 mt-2">
                            <li>Properties are imported from the Redefine API (api.redefine.co.za)</li>
                            <li>Filter by sector (Retail, Office, etc.) before fetching</li>
                            <li>Each property is linked by its Redefine ID and Slug for future updates</li>
                            <li>New properties are created with <strong>Private</strong> status</li>
                            <li>Up to 5 images per property are downloaded and stored in Firebase Storage</li>
                            <li>Property descriptions are imported from the API</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
